import { useState, useEffect } from "react";
import { Select, Progress } from "@backstage/core-components";
import { Alert } from "@material-ui/lab";
import { 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  Button 
} from "@material-ui/core";
import { 
  Folder as RepoIcon, 
  Build, 
  Publish, 
  PolicyOutlined, 
  CloudDownload,
  Code
} from "@material-ui/icons";

// Import types
import { 
  Repository,
  RepositoryHealthDetails,
  ProjectPipelineData,
  ProjectReleaseData,
  ProjectBranchPolicyReport,
  FilteredProjectDeploymentData,
  ProjectBranchNamingReport,
  PipelineInfo,
  Release,
  BranchPolicyCompliance,
  ProcessedDeployment,
  BranchNamingCompliance
} from './types';

// Import hooks
import {
  useProjects,
  useRepositories,
  useRepositoryHealth,
  usePipelineData,
  useReleaseData,
  usePolicyReport,
  useDeploymentData,
  useBranchNamingReport,
  useSelectedItems
} from './hooks';

// Import utils
import { testBackendConnection } from './utils';

// Import components
import { RepositoryList, RepositoryDetail } from './components/repository';
import { PipelineOverviewCards, PipelineTabs, PipelineDetail } from './components/pipeline';
import { ReleaseOverviewCards, ReleaseTabs, ReleaseDetail } from './components/release';
import { PolicyOverviewCards, PolicyTabs, PolicyDetail } from './components/policy';
import { DeploymentOverviewCards, DeploymentTabs, DeploymentDetail } from './components/deployment';
import { BranchNamingOverviewCards, BranchNamingTabs, BranchNamingDetail } from './components/branch-naming';

type ViewMode = 'initial' | 'repositories' | 'pipelines' | 'releases' | 'policies' | 'deployments' | 'branch-naming';

export const SwbcAdoRepoPluginPage = () => {
  // State management
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [debugInfo, setDebugInfo] = useState<string>('');
  const [baseUrl, setBaseUrl] = useState<string>('');
  const [tabValue, setTabValue] = useState(0);
  const [viewMode, setViewMode] = useState<ViewMode>('initial');

  const organization = "SWBC-FigWebDev";

  // Custom hooks
  const { projects, loading: projectsLoading, error: projectsError, fetchProjects } = useProjects(baseUrl, organization);
  const { repos, loading: reposLoading, error: reposError, fetchRepositories } = useRepositories(baseUrl, organization);
  const { repoHealthDetails, loading: healthLoading, error: healthError, fetchRepositoryDetails, setRepoHealthDetails } = useRepositoryHealth(baseUrl, organization);
  const { pipelineData, loading: pipelineLoading, error: pipelineError, fetchPipelineData, setPipelineData } = usePipelineData(baseUrl, organization);
  const { releaseData, loading: releaseLoading, error: releaseError, fetchReleaseData, setReleaseData } = useReleaseData(baseUrl, organization);
  const { policyReport, loading: policyLoading, error: policyError, fetchPolicyReport, setPolicyReport } = usePolicyReport(baseUrl, organization);
  const { deploymentData, loading: deploymentLoading, error: deploymentError, fetchDeploymentData, setDeploymentData } = useDeploymentData(baseUrl, organization);
  const { namingReport, loading: namingLoading, error: namingError, fetchBranchNamingReport, setNamingReport } = useBranchNamingReport(baseUrl, organization);
  
  const {
    selectedRepository,
    setSelectedRepository,
    selectedPipeline,
    setSelectedPipeline,
    selectedRelease,
    setSelectedRelease,
    selectedPolicyRepository,
    setSelectedPolicyRepository,
    selectedDeployment,
    setSelectedDeployment,
    selectedNamingRepository,
    setSelectedNamingRepository,
    clearAllSelections
  } = useSelectedItems();

  // Initialize component
  useEffect(() => {
    const initializeComponent = async () => {
      const workingBaseUrl = await testBackendConnection();
      
      if (workingBaseUrl !== null) {
        setBaseUrl(workingBaseUrl);
        setDebugInfo('Backend connected successfully');
        await fetchProjects(workingBaseUrl);
      } else {
        setDebugInfo('Backend connection failed. Check console for details.');
      }
    };
    
    initializeComponent();
  }, []);

  // Handle project change
  const handleProjectChange = async (project: string) => {
    console.log('[FRONTEND] Project changed to:', project);
    
    if (!project || project.trim() === '') {
      return;
    }
    
    setSelectedProject(project);
    clearAllSelections();
    setViewMode('initial');
    setDebugInfo(`🔄 Loading data for project ${project}...`);

    try {
      // Load repositories
      await fetchRepositories(project);
      setDebugInfo(`✓ Repositories loaded. Loading pipelines...`);
      
      // Load pipeline data
      await fetchPipelineData(project);
      setDebugInfo(`✓ Repositories and pipelines loaded. Loading releases...`);
      
      // Load release data
      await fetchReleaseData(project);
      setDebugInfo(`✓ Data loading complete. Loading policies...`);

      // Load policy report
      await fetchPolicyReport(project);
      setDebugInfo(`✓ Policies loaded. Loading deployments...`);

      // Load deployment data
      await fetchDeploymentData(project);
      setDebugInfo(`✓ Deployments loaded. Loading branch naming standards...`);

      // Load branch naming report
      await fetchBranchNamingReport(project);
      setDebugInfo(`✅ All data loaded successfully for ${project}`);
      
    } catch (error) {
      console.error('[FRONTEND] Error loading project data:', error);
      setDebugInfo(`❌ Error loading data for ${project}: ${error}`);
    }
  };

  // Handle view mode change
  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    clearAllSelections();
    setTabValue(0);
  };

  // Handle repository selection
  const handleRepositorySelect = async (repository: Repository) => {
    console.log('[FRONTEND] Repository selected:', repository.name);
    
    setSelectedRepository(repository);
    setTabValue(0);

    await fetchRepositoryDetails(selectedProject, repository);
  };

  // Handle back navigation
  const handleBackToList = () => {
    clearAllSelections();
    setTabValue(0);
  };

  const handleBackToProjectView = () => {
    setViewMode('initial');
    clearAllSelections();
    setTabValue(0);
  };

  // Handle tab change
  const handleTabChange = (_: React.ChangeEvent<{}>, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <div style={{ padding: '20px' }}>
      <h1>Azure DevOps Project Dashboard</h1>
      <p>Organization: <strong>{organization}</strong></p>

      <Alert severity="info" style={{ marginBottom: '20px' }}>
        <strong>Status:</strong> {debugInfo || 'Initializing...'}
      </Alert>

      {projectsLoading && (
        <div style={{ marginBottom: '20px' }}>
          <Progress />
          <p>Loading projects...</p>
        </div>
      )}

      {projectsError && (
        <Alert severity="warning" style={{ marginBottom: '20px' }}>
          <strong>Warning:</strong> {projectsError}
          <br />
          <small>Using fallback project list if available.</small>
        </Alert>
      )}

      <div style={{ marginBottom: '20px' }}>
        <Select
          label="Select an ADO Project"
          items={projects}
          selected={selectedProject}
          onChange={project => handleProjectChange(project as string)}
          disabled={projectsLoading}
          placeholder={projectsLoading ? "Loading projects..." : "Choose a project"}
        />
      </div>

      {/* Initial Project View - Show Options */}
      {selectedProject && viewMode === 'initial' && (
        <div>
          <Typography variant="h5" style={{ marginBottom: '24px' }}>
            Select Report Type for Project: <strong>{selectedProject}</strong>
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <Card 
                elevation={2} 
                style={{ cursor: 'pointer', height: '200px' }}
                onClick={() => handleViewModeChange('repositories')}
              >
                <CardContent style={{ textAlign: 'center', padding: '30px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <RepoIcon style={{ fontSize: 60, color: '#2196f3', marginBottom: '16px' }} />
                  <Typography variant="h6" style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                    Repository View
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Explore repositories, branches, contributors, and code statistics
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <Card 
                elevation={2} 
                style={{ cursor: 'pointer', height: '200px' }}
                onClick={() => handleViewModeChange('pipelines')}
              >
                <CardContent style={{ textAlign: 'center', padding: '30px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Build style={{ fontSize: 60, color: '#4caf50', marginBottom: '16px' }} />
                  <Typography variant="h6" style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                    Pipeline Dashboard
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    View build pipelines, success rates, and deployment metrics
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <Card 
                elevation={2} 
                style={{ cursor: 'pointer', height: '200px' }}
                onClick={() => handleViewModeChange('releases')}
              >
                <CardContent style={{ textAlign: 'center', padding: '30px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Publish style={{ fontSize: 60, color: '#ff9800', marginBottom: '16px' }} />
                  <Typography variant="h6" style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                    Release Report
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Track releases, deployments, and delivery metrics
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <Card 
                elevation={2} 
                style={{ cursor: 'pointer', height: '200px' }}
                onClick={() => handleViewModeChange('policies')}
              >
                <CardContent style={{ textAlign: 'center', padding: '30px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <PolicyOutlined style={{ fontSize: 60, color: '#9c27b0', marginBottom: '16px' }} />
                  <Typography variant="h6" style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                    Branch Policies Report
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Review branch protection rules and compliance status
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={2}>
              <Card 
                elevation={2} 
                style={{ cursor: 'pointer', height: '200px' }}
                onClick={() => handleViewModeChange('deployments')}
              >
                <CardContent style={{ textAlign: 'center', padding: '30px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <CloudDownload style={{ fontSize: 60, color: '#ff5722', marginBottom: '16px' }} />
                  <Typography variant="h6" style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                    Deployments Dashboard
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Track current week deployments, past 4 weeks activity, and daily status
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={2}>
              <Card 
                elevation={2} 
                style={{ cursor: 'pointer', height: '200px' }}
                onClick={() => handleViewModeChange('branch-naming')}
              >
                <CardContent style={{ textAlign: 'center', padding: '30px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Code style={{ fontSize: 60, color: '#607d8b', marginBottom: '16px' }} />
                  <Typography variant="h6" style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                    Branch Naming Standards
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Validate branch names and check for uppercase violations
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </div>
      )}

      {/* Back to Project View Button */}
      {selectedProject && viewMode !== 'initial' && !selectedRepository && !selectedPipeline && !selectedRelease && !selectedPolicyRepository && !selectedDeployment && !selectedNamingRepository && (
        <div style={{ marginBottom: '20px' }}>
          <Button 
            variant="outlined" 
            onClick={handleBackToProjectView}
            style={{ marginRight: '10px' }}
          >
            ← Back to Project Options
          </Button>
        </div>
      )}

      {/* Navigation buttons for detailed views */}
      {(selectedRepository || selectedPipeline || selectedRelease || selectedPolicyRepository || selectedDeployment || selectedNamingRepository) && (
        <div style={{ marginBottom: '20px' }}>
          <Button 
            variant="outlined" 
            onClick={handleBackToList}
            style={{ marginRight: '10px' }}
          >
            ← Back to List
          </Button>
        </div>
      )}

      {/* Error displays */}
      {reposError && (
        <Alert severity="error" style={{ marginBottom: '20px' }}>
          <strong>Repository Error:</strong> {reposError}
        </Alert>
      )}

      {pipelineError && (
        <Alert severity="error" style={{ marginBottom: '20px' }}>
          <strong>Pipeline Error:</strong> {pipelineError}
        </Alert>
      )}

      {releaseError && (
        <Alert severity="error" style={{ marginBottom: '20px' }}>
          <strong>Release Error:</strong> {releaseError}
        </Alert>
      )}

      {policyError && (
        <Alert severity="error" style={{ marginBottom: '20px' }}>
          <strong>Policy Error:</strong> {policyError}
        </Alert>
      )}

      {deploymentError && (
        <Alert severity="error" style={{ marginBottom: '20px' }}>
          <strong>Deployment Error:</strong> {deploymentError}
        </Alert>
      )}

      {namingError && (
        <Alert severity="error" style={{ marginBottom: '20px' }}>
          <strong>Branch Naming Error:</strong> {namingError}
        </Alert>
      )}

      {/* REPOSITORY VIEWS */}
      {selectedProject && viewMode === 'repositories' && !selectedRepository && (
        <div>
          {reposLoading ? (
            <div style={{ marginBottom: '20px' }}>
              <Progress />
              <p>Loading repositories...</p>
            </div>
          ) : repos.length > 0 ? (
            <RepositoryList
              repositories={repos}
              onRepositorySelect={handleRepositorySelect}
            />
          ) : (
            <Alert severity="info">
              No repositories found for project: <strong>{selectedProject}</strong>
            </Alert>
          )}
        </div>
      )}

      {/* Repository Detail View */}
      {selectedRepository && repoHealthDetails && (
        <div>
          {healthLoading ? (
            <div style={{ marginBottom: '20px' }}>
              <Progress />
              <p>Loading repository details...</p>
            </div>
          ) : (
            <RepositoryDetail
              repository={selectedRepository}
              repoHealthDetails={repoHealthDetails}
              tabValue={tabValue}
              onTabChange={handleTabChange}
            />
          )}
        </div>
      )}

      {/* PIPELINE VIEWS */}
      {selectedProject && viewMode === 'pipelines' && !selectedPipeline && (
        <div>
          {pipelineLoading ? (
            <div style={{ marginBottom: '20px' }}>
              <Progress />
              <p>Loading pipeline data...</p>
            </div>
          ) : pipelineData ? (
            <div>
              <PipelineOverviewCards pipelineData={pipelineData} />
              <PipelineTabs
                pipelineData={pipelineData}
                tabValue={tabValue}
                onTabChange={handleTabChange}
                onPipelineSelect={setSelectedPipeline}
              />
            </div>
          ) : (
            <Typography variant="body1" color="textSecondary">
              No pipeline data available for this project.
            </Typography>
          )}
        </div>
      )}

      {/* Pipeline Detail View */}
      {selectedPipeline && (
        <PipelineDetail pipeline={selectedPipeline} />
      )}

      {/* RELEASE VIEWS */}
      {selectedProject && viewMode === 'releases' && !selectedRelease && (
        <div>
          {releaseLoading ? (
            <div style={{ marginBottom: '20px' }}>
              <Progress />
              <p>Loading release data...</p>
            </div>
          ) : releaseData ? (
            <div>
              <ReleaseOverviewCards releaseData={releaseData} />
              <ReleaseTabs
                releaseData={releaseData}
                tabValue={tabValue}
                onTabChange={handleTabChange}
                onReleaseSelect={setSelectedRelease}
              />
            </div>
          ) : (
            <Typography variant="body1" color="textSecondary">
              No release data available for this project.
            </Typography>
          )}
        </div>
      )}

      {/* Release Detail View */}
      {selectedRelease && (
        <ReleaseDetail release={selectedRelease} />
      )}

      {/* POLICY VIEWS */}
      {selectedProject && viewMode === 'policies' && !selectedPolicyRepository && (
        <div>
          {policyLoading ? (
            <div style={{ marginBottom: '20px' }}>
              <Progress />
              <p>Loading branch policy report...</p>
            </div>
          ) : policyReport ? (
            <div>
              <PolicyOverviewCards policyReport={policyReport} />
              <PolicyTabs
                policyReport={policyReport}
                tabValue={tabValue}
                onTabChange={handleTabChange}
                onRepositorySelect={setSelectedPolicyRepository}
              />
            </div>
          ) : (
            <Typography variant="body1" color="textSecondary">
              No branch policy data available for this project.
            </Typography>
          )}
        </div>
      )}

      {/* Policy Detail View */}
      {selectedPolicyRepository && (
        <PolicyDetail repository={selectedPolicyRepository} />
      )}

      {/* DEPLOYMENT VIEWS */}
      {selectedProject && viewMode === 'deployments' && !selectedDeployment && (
        <div>
          {deploymentLoading ? (
            <div style={{ marginBottom: '20px' }}>
              <Progress />
              <p>Loading Beta/Prod deployment data...</p>
            </div>
          ) : deploymentData ? (
            <div>
              <DeploymentOverviewCards deploymentData={deploymentData} />
              <DeploymentTabs
                deploymentData={deploymentData}
                tabValue={tabValue}
                onTabChange={handleTabChange}
                onDeploymentSelect={setSelectedDeployment}
              />
            </div>
          ) : (
            <Typography variant="body1" color="textSecondary">
              No Beta/Production deployment data available for this project.
            </Typography>
          )}
        </div>
      )}

      {/* Deployment Detail View */}
      {selectedDeployment && (
        <DeploymentDetail deployment={selectedDeployment} />
      )}

      {/* BRANCH NAMING VIEWS */}
      {selectedProject && viewMode === 'branch-naming' && !selectedNamingRepository && (
        <div>
          {namingLoading ? (
            <div style={{ marginBottom: '20px' }}>
              <Progress />
              <p>Loading branch naming standards report...</p>
            </div>
          ) : namingReport ? (
            <div>
              <BranchNamingOverviewCards namingReport={namingReport} />
              <BranchNamingTabs
                namingReport={namingReport}
                tabValue={tabValue}
                onTabChange={handleTabChange}
                onRepositorySelect={setSelectedNamingRepository}
              />
            </div>
          ) : (
            <Typography variant="body1" color="textSecondary">
              No branch naming data available for this project.
            </Typography>
          )}
        </div>
      )}

      {/* Branch Naming Detail View */}
      {selectedNamingRepository && (
        <BranchNamingDetail repository={selectedNamingRepository} />
      )}
    </div>
  );
};